import type { ChatMessage } from "./types";

// sessionStorage keyed by agent. Survives nav within a tab, clears on close.
// Wrapped in try/catch — sessionStorage throws in private mode on some browsers.

const keyFor = (agent: string) => `chatporch:${agent}`;
const cidKeyFor = (agent: string) => `chatporch:cid:${agent}`;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function loadMessages(agent: string): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(keyFor(agent));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is ChatMessage =>
        m && typeof m === "object" &&
        (m.role === "user" || m.role === "assistant") &&
        typeof m.content === "string",
    );
  } catch {
    return [];
  }
}

export function saveMessages(agent: string, messages: ChatMessage[]): void {
  try {
    sessionStorage.setItem(keyFor(agent), JSON.stringify(messages));
  } catch {
    /* swallow quota/private-mode errors */
  }
}

// Conversation ID — UUID v4 generated on first use, persisted alongside
// messages so refreshes / SPA nav within the tab keep posting to the same
// row. Clearing chat (storage.clear in dialog.ts) also rotates this so the
// next turn starts a brand new conversation in the log.
export function loadOrCreateConversationId(agent: string): string {
  try {
    const existing = sessionStorage.getItem(cidKeyFor(agent));
    if (existing && UUID_RE.test(existing)) return existing;
  } catch {
    /* fall through to generate */
  }
  const id = newUuid();
  try {
    sessionStorage.setItem(cidKeyFor(agent), id);
  } catch {
    /* swallow quota/private-mode errors */
  }
  return id;
}

export function clearConversationId(agent: string): void {
  try {
    sessionStorage.removeItem(cidKeyFor(agent));
  } catch {
    /* swallow */
  }
}

// crypto.randomUUID is on HTTPS contexts in every modern browser; the embed
// only loads from embed.chatporch.com, which is HTTPS-only. Fallback covers
// http://localhost dev pages where randomUUID is still available, plus
// pathologically old WebViews — the result is good enough for a PK that
// only needs to be unique within a sessionStorage scope.
function newUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const hex = "0123456789abcdef";
  let out = "";
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) out += "-";
    else if (i === 14) out += "4";
    else if (i === 19) out += hex[(Math.random() * 4) | 8];
    else out += hex[(Math.random() * 16) | 0];
  }
  return out;
}
